import { WeekPicker } from 'app/components/common/DatePickers';
import { AgenturNameCell } from 'app/components/shared/AgenturName.cell';
import { Table } from 'app/components/table/table';
import { ITableColumn, TableModel, TableRow } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { AkumaAktionenVM } from 'app/stores/ui/agt.akumaaktionen.ui.store';
import { formatPercentTwoDig } from 'app/utils';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { useHistoryWithQuery } from 'app/hooks/useHistoryWithQuery';
import _ from 'lodash';
import { schemaAkumaAktionenOverview } from 'app/stores/agt.akumaaktionen.store';
import { AkumaAktionenSankeyChart } from './AkumaAktionenSankeyChart';
import { Modal } from 'app/components/Modal';

export const AkumaAktionenOptions = observer(() => {
	const { agtAkumaAktionenUiStore } = useStore();

	const onBmChange = (value: number) => {
		runInAction(() => {
			agtAkumaAktionenUiStore.setKw(value);
		});
	};

	const bmOptions = agtAkumaAktionenUiStore.availableKws.map((bm) => ({
		value: bm,
		label: `${Math.floor(bm / 100)}/${bm % 100}`,
	}));

	return <WeekPicker value={agtAkumaAktionenUiStore.selectedKw} opts={bmOptions} onChange={onBmChange} emptyText="Alle Monate" queryParam="bm" />;
});

export const AkumaAktionenTreffer = observer(() => {
	const { berichteUiiStore, agtAkumaAktionenUiStore, agenturListUiStore, uiStore } = useStore();
	const history = useHistoryWithQuery();
	const [dataLoaded, setDataLoaded] = useState(false);

	const tm = useMemo(() => {
		const tm = new TableModel<AkumaAktionenVM, number>();
		const cols: ITableColumn<AkumaAktionenVM, number>[] = [];

		cols.push({
			label: 'Agentur',
			path: 'agt.name',
			sortBy: 'agt.name',
			render: AgenturNameCell,
		});

		schemaAkumaAktionenOverview.forEach((s) => {
			cols.push({
				label: s.label + ' (Treffer)',
				path: 'overviewItem.' + s.name + 'CustumerCount',
			});
			cols.push({
				label: s.label + ' Bearbeitet',
				path: 'overviewItem.' + s.name + 'AgencyCount',
			});
			cols.push({
				label: s.label + ' Quote',
				path: 'overviewItem.' + s.name + 'Quote',
				format: formatPercentTwoDig,
			});
		});

		tm.onRowClick = (row: TableRow<AkumaAktionenVM>) => {
			history.pushWithQuery({
				search: '?agtId=' + row.data.agt.agtId,
			});
		};

		tm.sortBy = 'agt.name';
		tm.setCols(cols);

		tm.idProperty = 'agt.agtId';
		tm.idType = 'number';

		return tm;
	}, []);

	useEffect(() => {
		setDataLoaded(false);
		const agtId = berichteUiiStore.selectedAgt?.agtId;
		Promise.all([agtAkumaAktionenUiStore.load(), agtAkumaAktionenUiStore.loadCampaignCountOverviewByBm(agtAkumaAktionenUiStore.selectedKw, agtId)]).then(() => {
			let items = agtAkumaAktionenUiStore.items;
			if (berichteUiiStore.selectedAgt) {
				const agtId = berichteUiiStore.selectedAgt.agtId;
				items = items.filter((i) => i.agt.agtId === agtId);
				items = _.sortBy(items, 'bm').reverse();
			} else {
				items = agtAkumaAktionenUiStore.applyFilter(items);
			}

			tm.setRowData(items);
			tm.sort();
			setDataLoaded(true);
		});
	}, [
		tm,
		berichteUiiStore.selectedAgt,
		agenturListUiStore.filterHasChanged,
		agenturListUiStore.currentFilter.filtersWithValue.length,
		agtAkumaAktionenUiStore.selectedKw,
		agtAkumaAktionenUiStore.items,
	]);

	const renderSankeyChart = () => {
		if (!dataLoaded) {
			return null;
		}

		return (
			<>
				<div className="pad-1rem">
					<AkumaAktionenSankeyChart key={`sankey-${agtAkumaAktionenUiStore.selectedKw}-${dataLoaded}`} />
				</div>
			</>
		);
	};

	return (
		<>
			<div className="table-container pad-bottom-3rem">
				<AkumaAktionenOptions />
				<div className="table-container">
					<div className="pad-1rem">{renderSankeyChart()}</div>
					<Table stickyHeader={true} tm={tm} stickyFooter={true}></Table>
				</div>
			</div>
			<Modal modalId={'akumaAktionenLegend'} title={'Erläuterung der Dargestellten Kennzahlen'} onClose={() => uiStore.hideModal('akumaAktionenLegend')}>
				<div className="content">
					<ul>
						<li>
							<strong>Top-Aktionen:</strong> Aktionen die als Top-Aktionen markiert sind
						</li>
						<li>
							<strong>Top-Aktionen Bearbeitet:</strong> Zu mind. 5% des Aktionspotenzials wurden Aufgaben in AMIS.NOW erstellt oder ein PDF-Export der Aktionsliste ist erfolgt
						</li>
						<li>
							<strong>Top-Aktionen Quote:</strong> Quote der bearbeiten Top-Aktionen im Verhältnis zur Gesamtanzahl aller Top-Aktionen. Mind. 75% der Top-Aktionen sollen bearbeitet
							werden (Ambitionsniveau)
						</li>
						<li>
							<strong>Aktionen Download:</strong> Export der PDF-Kundenliste zur Aktion hat stattgefunden
						</li>
						<li>
							<strong>Nicht bearbeitet:</strong> Aktionen, die noch nicht bearbeitet wurden
						</li>
						<li>
							<strong>Aktionen:</strong> alle weiteren Aktionen, die keine Top-Aktion sind
						</li>
						<li>
							<strong>Aktionen bearbeitet:</strong> weitere Aktionen (nicht Top-Aktionen) in denen zu mind. 5% des Aktionspotenzials Aufgaben in AMIS.NOW erstellt oder ein PDF-Export der
							Aktionsliste erfolgt ist
						</li>
						<li>
							<strong>Leben/ Sach/ Kranken/ Übergreifend:</strong> Aufteilung nach Versicherungsparten
						</li>
					</ul>
				</div>
			</Modal>
		</>
	);
});
